// api.js
import axios from 'axios';

/**
 * Decide when to use mock data:
 * - Explicit via REACT_APP_USE_MOCK
 * - Otherwise: default to mock on localhost, real API on live
 */
const isLocalhost = typeof window !== 'undefined' &&
  (/^localhost$|^127\.0\.0\.1$|^0\.0\.0\.0$/.test(window.location.hostname));

const USE_MOCK = (process.env.REACT_APP_USE_MOCK ?? (isLocalhost ? '1' : '0')) === '1';

/**
 * Infer a safe API base:
 * Priority:
 * 1) REACT_APP_API_BASE (if provided)
 * 2) If on petrahcollection.co.ke -> same-origin (avoids CORS): "" (i.e., relative)
 * 3) Fallback to local backend http://localhost:8080
 *
 * Notes:
 * - Using a relative base ("") on live ensures requests go to https://petrahcollection.co.ke/*,
 *   preventing mixed-content and CORS problems.
 * - On localhost we point to your local API port.
 */
function inferApiBase() {
  const envBase = process.env.REACT_APP_API_BASE?.trim();
  if (envBase) return envBase.replace(/\/+$/, ''); // strip trailing slash

  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname.endsWith('petrahcollection.co.ke')) {
      // Same-origin requests on live (no baseURL => relative)
      return ''; // so axios hits /api/... on the same domain
    }
  }

  // Dev fallback
  return 'http://localhost:8080';
}

export const API_BASE = inferApiBase();

// Create the axios client
const live = axios.create({
  baseURL: API_BASE, // "" on live -> same-origin; full URL on localhost
  timeout: 12000,
  withCredentials: false, // set to true only if your API uses cookies/sessions
  headers: {
    'Accept': 'application/json',
  },
});

/* --------------------------- MOCK LAYER (DEV) --------------------------- */

async function mockGet(path, params = {}) {
  // map API routes to public/data files for local dev
  const base = process.env.PUBLIC_URL || '';
  const toUrl = (name) => `${base}/data/${name}`;

  if (path.startsWith('/api/products')) {
    const url = new URL('http://x' + path.replace('/api/products', '/api/products'));
    const q = Object.fromEntries(new URLSearchParams(url.search));
    const res = await fetch(toUrl('products.json'));
    const all = await res.json();
    let filtered = all;

    if (q.q) {
      const needle = q.q.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(needle) ||
          p.description.toLowerCase().includes(needle)
      );
    }
    if (q.category) {
      filtered = filtered.filter((p) => p.categorySlug === q.category);
    }
    if (q.brand) {
      filtered = filtered.filter((p) => p.brandSlug === q.brand);
    }
    if (q.sort === 'price-asc')
      filtered = filtered.slice().sort((a, b) => (a.offerPrice || a.price) - (b.offerPrice || b.price));
    if (q.sort === 'price-desc')
      filtered = filtered.slice().sort((a, b) => (b.offerPrice || b.price) - (a.offerPrice || a.price));

    const page = parseInt(q.page || '1', 10);
    const limit = parseInt(q.limit || '12', 10);
    const total = filtered.length;
    const start = (page - 1) * limit;
    const items = filtered.slice(start, start + limit);

    return { items, page, total, limit };
  }

  if (path.startsWith('/api/products/')) {
    const slug = path.split('/').pop();
    const res = await fetch(toUrl('products.json'));
    const all = await res.json();
    const item = all.find((p) => p.slug === slug);
    if (!item) throw new Error('Not found');
    return item;
  }

  if (path.startsWith('/api/categories')) {
    const res = await fetch(toUrl('categories.json'));
    return await res.json();
  }
  if (path.startsWith('/api/brands')) {
    const res = await fetch(toUrl('brands.json'));
    return await res.json();
  }
  if (path.startsWith('/api/home')) {
    const res = await fetch(toUrl('home.json'));
    return await res.json();
  }
  if (path.startsWith('/api/discount/')) {
    const code = path.split('/').pop().toUpperCase();
    if (code === 'WELCOME10') return { code, type: 'percent', value: 10, description: '10% off' };
    if (code === 'LESS500') return { code, type: 'amount', value: 500, description: 'KSh 500 off' };
    throw new Error('Invalid code');
  }

  throw new Error('Unknown mock path ' + path);
}

/* ------------------------------ HELPERS -------------------------------- */

const GET = async (path, params) => {
  if (USE_MOCK) return mockGet(withQuery(path, params));
  const res = await live.get(path, params ? { params } : undefined);
  return res.data;
};

function withQuery(path, params) {
  if (!params || Object.keys(params).length === 0) return path;
  const qs = new URLSearchParams(params).toString();
  return `${path}?${qs}`;
}

/* ------------------------------ EXPORTS -------------------------------- */

export async function getCategories() {
  return GET('/api/categories');
}

export async function getBrands() {
  return GET('/api/brands');
}

export async function getHome() {
  return GET('/api/home');
}

export async function searchProducts(params) {
  return GET('/api/products', params);
}

export async function getProduct(slug) {
  return GET(`/api/products/${slug}`);
}

export async function checkDiscount(code) {
  return GET(`/api/discount/${encodeURIComponent(code)}`);
}

/* ---------------------------- Usage Notes -------------------------------
1) LIVE (https://petrahcollection.co.ke/)
   - Do nothing. The code detects the domain and uses same-origin requests to /api/*.
   - No mixed-content or CORS headaches.

2) LOCAL DEV
   - Start your backend at http://localhost:8080 (or override).
   - Default uses mock data (PUBLIC_URL/data/*.json). To hit your local API, set:
       REACT_APP_USE_MOCK=0
     Optionally specify a custom base:
       REACT_APP_API_BASE=http://localhost:8080

3) If your live API is at a sub-path (e.g., https://petrahcollection.co.ke/backend),
   set:
       REACT_APP_API_BASE=https://petrahcollection.co.ke/backend
------------------------------------------------------------------------- */
